
import React, { useState, useEffect } from 'react';
import { StudentData, ReportTemplate, HeaderSettings, INITIAL_HEADER_SETTINGS } from './types';
import { School, Calendar, User, GraduationCap, Award, Sparkles } from 'lucide-react';
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { CBCReportTemplate } from './CBCReportTemplate';
// CustomHeader removed as unused
import { generatePdfHeader } from './pdfHeaderGenerator';
import { db, auth } from '../../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

interface ReportPreviewProps {
  data: StudentData;
  template?: ReportTemplate;
  id?: string;
  gapAdjust?: number; // Control for header gap adjustment
}

const calculateGrade = (score: number, total: number): string => {
  const percentage = (score / total) * 100;
  if (percentage >= 97) return 'A+';
  if (percentage >= 90) return 'A';
  if (percentage >= 80) return 'B';
  if (percentage >= 70) return 'C';
  if (percentage >= 60) return 'D';
  return 'F';
};



// Theme configuration interface
interface ThemeConfig {
  container: string;
  headerWrapper: string;
  schoolTitle: string;
  subTitle: string;
  infoCard: string;
  infoLabel: string;
  infoValue: string;
  tableContainer: string;
  tableHead: string;
  tableHeadText: string;
  tableRow: string;
  tableRowAlt: string; // For alternate rows or hover
  gradeBadge: (grade: string) => string;
  sectionTitle: string;
  aiSection: string;
  remarksSection: string;
  footer: string;
  font: string;
}

// Helper for default colored grades
const defaultGradeColor = (grade: string) => {
    switch (grade) {
      case 'A+':
      case 'A': return 'text-emerald-600 font-bold';
      case 'B': return 'text-blue-600 font-semibold';
      case 'C': return 'text-yellow-600';
      case 'D': return 'text-orange-600';
      case 'F': return 'text-red-600 font-bold';
      default: return 'text-slate-900';
    }
};

const monochromeGradeColor = (grade: string) => {
     switch (grade) {
      case 'A+':
      case 'A': return 'text-slate-900 font-bold underline';
      case 'F': return 'text-slate-900 font-bold italic';
      default: return 'text-slate-800';
    }
};

// --- THEME DEFINITIONS ---
// Optimized for Print Compactness (Unified for Screen & Print)
const THEMES: Record<string, ThemeConfig> = {
    default: {
        container: "bg-white p-8 shadow-2xl text-slate-800 text-xs",
        font: "font-sans",
        headerWrapper: "border-b-4 border-indigo-900 pb-1 mb-1 flex justify-between items-center",
        schoolTitle: "text-xl font-serif font-bold text-indigo-900 uppercase tracking-wide",
        subTitle: "text-[10px] text-slate-500 font-medium uppercase tracking-widest mt-1",
        infoCard: "bg-slate-50 rounded-xl p-2 mb-2 border border-slate-100",
        infoLabel: "flex items-center gap-2 text-xs font-bold text-indigo-600 uppercase mb-1",
        infoValue: "text-sm font-semibold truncate",
        tableContainer: "overflow-hidden border border-slate-200 rounded-lg mb-2",
        tableHead: "bg-slate-100 border-b border-slate-200",
        tableHeadText: "uppercase font-bold text-[10px] text-slate-600",
        tableRow: "hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0",
        tableRowAlt: "",
        gradeBadge: defaultGradeColor,
        sectionTitle: "text-sm font-bold text-slate-800 mb-1 uppercase tracking-wide border-l-4 border-indigo-500 pl-3",
        aiSection: "mb-2 bg-gradient-to-br from-indigo-50 to-white p-2 rounded-xl border border-indigo-100 relative overflow-hidden",
        remarksSection: "p-2 border border-dashed border-slate-300 rounded-lg min-h-[40px] text-sm text-slate-700 bg-slate-50/50",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center"
    },
    minimal: {
        container: "bg-white p-8 shadow-xl text-black text-xs",
        font: "font-sans",
        headerWrapper: "border-b border-black pb-1 mb-1 flex flex-col md:flex-row justify-between items-start md:items-end gap-4",
        schoolTitle: "text-xl font-bold tracking-tighter text-black",
        subTitle: "text-[10px] font-normal uppercase tracking-[0.2em] text-slate-600 mt-2",
        infoCard: "mb-2",
        infoLabel: "text-[10px] uppercase font-bold text-slate-400 tracking-widest mb-0.5",
        infoValue: "text-sm font-light text-black",
        tableContainer: "mb-2",
        tableHead: "border-b-2 border-black",
        tableHeadText: "uppercase font-bold text-[10px] text-black tracking-wider",
        tableRow: "border-b border-slate-100",
        tableRowAlt: "",
        gradeBadge: monochromeGradeColor,
        sectionTitle: "text-xs font-bold uppercase tracking-widest border-b border-slate-200 pb-2 mb-1 text-black",
        aiSection: "mb-2 p-2 border border-slate-200 bg-slate-50",
        remarksSection: "p-0 border-b border-black min-h-[30px] text-sm text-black italic",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center font-light"
    },
    formal: {
        container: "bg-[#fffdf5] p-8 shadow-2xl text-slate-900 border-4 border-double border-slate-800 text-xs",
        font: "font-serif",
        headerWrapper: "text-center border-b border-slate-400 pb-1 mb-1",
        schoolTitle: "text-xl font-serif font-bold text-slate-900 uppercase tracking-widest",
        subTitle: "text-[10px] text-slate-600 italic mt-2 font-serif",
        infoCard: "bg-transparent mb-2 border-y border-slate-300 py-2",
        infoLabel: "block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 text-center",
        infoValue: "text-sm text-center text-slate-800",
        tableContainer: "mb-2 border border-slate-800",
        tableHead: "bg-slate-200 border-b border-slate-800",
        tableHeadText: "uppercase font-bold text-[10px] text-slate-800",
        tableRow: "border-b border-slate-300",
        tableRowAlt: "",
        gradeBadge: defaultGradeColor,
        sectionTitle: "text-center text-sm font-bold text-slate-800 mb-1 font-serif italic border-b border-slate-200 pb-2 mx-auto w-1/2",
        aiSection: "mb-2 p-2 border border-slate-300 bg-white italic",
        remarksSection: "p-2 border border-slate-400 min-h-[40px] text-sm text-slate-800 font-serif",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center"
    },
    corporate: {
        container: "bg-slate-50 p-8 shadow-2xl text-slate-800 text-xs",
        font: "font-sans",
        headerWrapper: "bg-slate-800 p-6 -mx-8 -mt-8 mb-2 text-white flex justify-between items-center shadow-md",
        schoolTitle: "text-lg font-bold text-white uppercase tracking-wide",
        subTitle: "text-xs text-slate-300 uppercase tracking-widest mt-1",
        infoCard: "bg-white shadow-sm rounded-sm p-2 mb-2 border-l-4 border-blue-600",
        infoLabel: "flex items-center gap-2 text-xs font-bold text-slate-500 uppercase mb-1",
        infoValue: "text-sm font-bold text-slate-700",
        tableContainer: "bg-white shadow-sm mb-2",
        tableHead: "bg-slate-800 text-white",
        tableHeadText: "uppercase font-bold text-[10px] text-white",
        tableRow: "border-b border-slate-200 even:bg-slate-50",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+','A'].includes(g) ? 'text-blue-700' : 'text-slate-700'}`,
        sectionTitle: "text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide",
        aiSection: "mb-2 bg-blue-50 p-2 border-l-4 border-blue-600 text-slate-700",
        remarksSection: "p-2 bg-white border border-slate-300 min-h-[40px] text-sm shadow-sm",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-sm"
    },
    creative: {
        container: "bg-white p-8 shadow-2xl text-slate-800 border-t-4 border-purple-500 text-xs",
        font: "font-sans",
        headerWrapper: "mb-2 flex justify-between items-center bg-purple-50 p-2 rounded-3xl",
        schoolTitle: "text-xl font-bold text-purple-600",
        subTitle: "text-[10px] text-purple-400 font-medium mt-1",
        infoCard: "mb-2",
        infoLabel: "text-xs font-bold text-purple-400 uppercase mb-1",
        infoValue: "text-sm font-bold text-slate-700 bg-purple-50 p-2 rounded-xl text-center",
        tableContainer: "overflow-hidden rounded-2xl border-2 border-purple-100 mb-2",
        tableHead: "bg-purple-500 text-white",
        tableHeadText: "uppercase font-bold text-[10px] text-white",
        tableRow: "border-b border-purple-50 hover:bg-purple-50/50",
        tableRowAlt: "",
        gradeBadge: (g) => `inline-block px-3 py-1 rounded-full text-[10px] font-bold ${['A+','A'].includes(g) ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'}`,
        sectionTitle: "text-sm font-bold text-purple-600 mb-1 flex items-center gap-2",
        aiSection: "mb-2 bg-gradient-to-r from-purple-500 to-pink-500 p-1 rounded-2xl text-white",
        remarksSection: "p-2 border-2 border-dashed border-purple-200 rounded-2xl min-h-[40px] text-sm text-slate-700 bg-purple-50/30",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-purple-900"
    },
    tech: {
        container: "bg-slate-900 p-8 shadow-2xl text-green-400 font-mono text-xs",
        font: "font-mono",
        headerWrapper: "border-b border-green-800 pb-2 mb-2 flex justify-between items-end",
        schoolTitle: "text-lg font-bold text-green-500 uppercase tracking-widest",
        subTitle: "text-xs text-green-700 uppercase tracking-widest mt-1",
        infoCard: "bg-slate-800 border border-green-900/50 p-2 mb-2",
        infoLabel: "text-xs text-green-600 uppercase mb-1",
        infoValue: "text-sm text-green-300",
        tableContainer: "border border-green-800 mb-2",
        tableHead: "bg-green-900/20 border-b border-green-800",
        tableHeadText: "uppercase font-bold text-[10px] text-green-500",
        tableRow: "border-b border-green-900/50 hover:bg-green-900/10",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+','A'].includes(g) ? 'text-green-400 text-shadow' : 'text-green-700'}`,
        sectionTitle: "text-xs font-bold text-green-600 mb-1 uppercase tracking-widest border-b border-green-900 w-fit pr-4",
        aiSection: "mb-2 bg-slate-800 p-2 border border-green-700 text-green-300 text-sm",
        remarksSection: "p-3 border border-green-800 bg-slate-950 min-h-[40px] text-sm text-green-500",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-green-800 text-xs"
    },
    elegant: {
        container: "bg-[#F9F7F2] p-8 shadow-xl text-slate-800 border-t-4 border-amber-700 text-xs",
        font: "font-serif",
        headerWrapper: "text-center mb-2",
        schoolTitle: "text-xl font-serif italic text-amber-900 mb-1",
        subTitle: "text-xs font-bold text-amber-700/60 uppercase tracking-[0.3em]",
        infoCard: "border-y border-amber-200 py-2 mb-2 bg-white/50",
        infoLabel: "text-xs font-bold text-amber-800/60 uppercase mb-1 tracking-wider text-center",
        infoValue: "text-sm text-amber-950 text-center font-serif",
        tableContainer: "mb-2",
        tableHead: "border-b-2 border-amber-800/20",
        tableHeadText: "uppercase font-bold text-[10px] text-amber-900 tracking-widest py-1",
        tableRow: "border-b border-amber-100 hover:bg-amber-50/50 transition-colors",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold font-serif ${['A+','A'].includes(g) ? 'text-amber-700' : 'text-slate-500'}`,
        sectionTitle: "text-sm italic font-serif text-amber-900 mb-1 text-center",
        aiSection: "mb-2 p-2 bg-white border border-amber-100 shadow-sm italic text-slate-600",
        remarksSection: "p-2 bg-white border border-amber-200 min-h-[40px] text-sm text-slate-700 font-serif shadow-sm",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-amber-900/50 font-serif italic"
    },
    nature: {
        container: "bg-stone-50 p-8 shadow-2xl text-stone-800 text-xs",
        font: "font-sans",
        headerWrapper: "bg-emerald-800 text-emerald-50 p-2 rounded-tl-[2rem] rounded-br-[2rem] mb-2 flex justify-between items-center",
        schoolTitle: "text-xl font-bold tracking-tight",
        subTitle: "text-emerald-200 text-[10px] font-medium uppercase tracking-widest",
        infoCard: "mb-2 bg-white/50 p-2 rounded-2xl border border-emerald-100/50 shadow-sm",
        infoLabel: "text-xs font-bold text-emerald-700 uppercase mb-1",
        infoValue: "text-sm text-stone-700 border-l-2 border-emerald-300 pl-3",
        tableContainer: "border-2 border-emerald-100 rounded-xl overflow-hidden mb-2",
        tableHead: "bg-emerald-50 border-b border-emerald-100",
        tableHeadText: "uppercase font-bold text-[10px] text-emerald-800",
        tableRow: "border-b border-emerald-50/50 hover:bg-emerald-50/30",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+','A'].includes(g) ? 'text-emerald-600' : 'text-stone-500'}`,
        sectionTitle: "text-sm font-bold text-emerald-800 mb-1 flex items-center gap-2",
        aiSection: "mb-2 bg-emerald-50/50 p-2 rounded-2xl border border-emerald-100 text-stone-700",
        remarksSection: "p-3 border border-stone-200 rounded-xl bg-white min-h-[40px] text-sm",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-stone-400"
    },
    bold: {
        container: "bg-white p-8 shadow-2xl text-black border-x-4 border-red-600 text-xs",
        font: "font-sans",
        headerWrapper: "border-b-4 border-black pb-2 mb-2",
        schoolTitle: "text-2xl font-black text-black uppercase tracking-tighter transform -skew-x-6",
        subTitle: "text-xs font-bold text-red-600 uppercase tracking-widest transform -skew-x-6",
        infoCard: "bg-black text-white p-2 mb-2 transform -skew-x-2",
        infoLabel: "text-xs font-bold text-red-500 uppercase mb-1",
        infoValue: "text-sm font-bold text-white",
        tableContainer: "mb-2 border-2 border-black",
        tableHead: "bg-red-600 text-white border-b-2 border-black",
        tableHeadText: "uppercase font-black text-xs italic",
        tableRow: "border-b-2 border-black font-bold",
        tableRowAlt: "",
        gradeBadge: (g) => `font-black italic ${['A+','A'].includes(g) ? 'text-red-600 text-lg' : 'text-black'}`,
        sectionTitle: "text-sm font-black text-black mb-1 uppercase italic bg-red-100 inline-block px-4 transform -skew-x-6",
        aiSection: "mb-2 border-2 border-black p-2 font-bold text-xs leading-relaxed",
        remarksSection: "p-3 border-2 border-red-600 min-h-[40px] text-sm font-bold bg-red-50",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center font-black uppercase"
    },
    rose: {
        container: "bg-[#fff1f2] p-8 shadow-2xl text-rose-900 border-t-4 border-rose-900 text-xs",
        font: "font-serif",
        headerWrapper: "border-b border-rose-200 pb-2 mb-2 flex justify-between items-end",
        schoolTitle: "text-xl font-serif font-bold text-rose-900",
        subTitle: "text-xs text-rose-500 font-bold uppercase tracking-widest mt-1",
        infoCard: "bg-white border border-rose-100 p-2 mb-2 shadow-sm",
        infoLabel: "text-xs font-bold text-rose-400 uppercase mb-1 tracking-wide",
        infoValue: "text-sm text-rose-950 font-serif",
        tableContainer: "mb-2 bg-white border border-rose-100",
        tableHead: "bg-rose-50 border-b border-rose-100",
        tableHeadText: "uppercase font-bold text-[10px] text-rose-800 tracking-wider",
        tableRow: "border-b border-rose-50 hover:bg-rose-50/30",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+','A'].includes(g) ? 'text-rose-700' : 'text-rose-400'}`,
        sectionTitle: "text-sm italic font-serif text-rose-800 mb-1 border-b border-rose-200 pb-1 w-fit pr-8",
        aiSection: "mb-2 bg-white p-2 border border-rose-100 text-rose-800 italic relative",
        remarksSection: "p-2 bg-white border border-rose-200 min-h-[40px] text-sm text-rose-800 font-serif",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-rose-300 font-serif italic"
    },
    blossom: {
        container: "bg-white p-8 shadow-xl text-slate-800 rounded-none border-0 text-xs",
        font: "font-sans",
        headerWrapper: "bg-pink-50 p-2 rounded-3xl mb-2 flex flex-col items-center justify-center text-center",
        schoolTitle: "text-xl font-bold text-pink-500 tracking-tight",
        subTitle: "text-xs text-pink-400 font-medium uppercase tracking-widest mt-1 bg-white px-3 py-1 rounded-full",
        infoCard: "mb-2",
        infoLabel: "text-[10px] font-bold text-pink-300 uppercase mb-1 text-center",
        infoValue: "text-sm text-slate-700 bg-pink-50/50 p-2 rounded-xl text-center border border-pink-100",
        tableContainer: "border border-pink-100 rounded-2xl overflow-hidden mb-2",
        tableHead: "bg-pink-400 text-white",
        tableHeadText: "uppercase font-bold text-[10px] text-white",
        tableRow: "border-b border-pink-50 hover:bg-pink-50/30",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold inline-block px-2 py-0.5 rounded-lg ${['A+','A'].includes(g) ? 'bg-pink-100 text-pink-600' : 'text-slate-500'}`,
        sectionTitle: "text-sm font-bold text-pink-500 mb-1 uppercase tracking-wide text-center",
        aiSection: "mb-2 bg-gradient-to-br from-pink-50 to-white p-2 rounded-3xl border border-pink-100 text-slate-600 text-center",
        remarksSection: "p-2 border border-pink-200 rounded-3xl bg-pink-50/20 min-h-[40px] text-sm text-slate-700 text-center",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-pink-300"
    },
    royal: {
        container: "bg-white p-8 shadow-2xl text-slate-900 border-4 border-double border-yellow-600 text-xs",
        font: "font-serif",
        headerWrapper: "bg-blue-950 p-2 mb-2 flex items-center justify-between text-yellow-50 border-b-4 border-yellow-600",
        schoolTitle: "text-xl font-serif font-bold text-yellow-500 uppercase tracking-widest",
        subTitle: "text-[10px] text-yellow-100 uppercase tracking-[0.2em] mt-1",
        infoCard: "bg-blue-50 border border-blue-100 p-2 mb-2",
        infoLabel: "text-[10px] font-bold text-blue-900 uppercase mb-1",
        infoValue: "text-sm font-bold text-blue-950",
        tableContainer: "mb-2 border border-blue-900",
        tableHead: "bg-blue-900 border-b-4 border-yellow-600",
        tableHeadText: "uppercase font-bold text-[10px] text-yellow-500 tracking-wider",
        tableRow: "border-b border-blue-100 hover:bg-blue-50",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+','A'].includes(g) ? 'text-blue-900' : 'text-slate-600'}`,
        sectionTitle: "text-sm font-bold text-blue-900 mb-1 uppercase tracking-wide border-b-2 border-yellow-500 w-fit",
        aiSection: "mb-2 bg-white p-2 border border-yellow-600/30 text-slate-800 shadow-sm",
        remarksSection: "p-3 border border-blue-200 bg-blue-50/30 min-h-[40px] text-sm text-blue-900 font-serif",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-blue-900 font-serif"
    },
    slate: {
        container: "bg-slate-50 p-8 shadow-xl text-slate-900 text-xs",
        font: "font-sans",
        headerWrapper: "border-b-2 border-slate-900 pb-2 mb-2 flex justify-between items-start",
        schoolTitle: "text-2xl font-black text-slate-900 tracking-tighter",
        subTitle: "text-[10px] font-bold text-slate-500 uppercase tracking-tight mt-1",
        infoCard: "bg-white p-2 border border-slate-200 mb-2 shadow-sm",
        infoLabel: "text-xs font-bold text-slate-400 uppercase mb-1",
        infoValue: "text-sm font-bold text-slate-900",
        tableContainer: "mb-2",
        tableHead: "bg-slate-900",
        tableHeadText: "uppercase font-bold text-[10px] text-white",
        tableRow: "border-b border-slate-200 bg-white hover:bg-slate-100",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold px-2 py-0.5 bg-slate-100 rounded ${['A+','A'].includes(g) ? 'text-slate-900' : 'text-slate-500'}`,
        sectionTitle: "text-sm font-black text-slate-900 mb-1 uppercase tracking-wider",
        aiSection: "mb-2 bg-slate-900 text-slate-300 p-2 font-light text-xs leading-relaxed",
        remarksSection: "p-2 bg-white border-2 border-slate-900 min-h-[40px] text-sm font-bold",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-slate-900 font-bold uppercase text-[10px]"
    },
    mint: {
        container: "bg-[#f0fdfa] p-8 shadow-xl text-teal-900 text-xs",
        font: "font-sans",
        headerWrapper: "mb-2 flex items-center gap-4 bg-teal-100/50 p-2 rounded-xl",
        schoolTitle: "text-xl font-bold text-teal-800",
        subTitle: "text-xs text-teal-600 uppercase tracking-widest mt-1",
        infoCard: "mb-2 bg-white/60 p-2 rounded-xl border border-teal-100",
        infoLabel: "text-xs font-bold text-teal-500 uppercase mb-1",
        infoValue: "text-sm font-semibold text-teal-900",
        tableContainer: "mb-2 border border-teal-100 rounded-lg overflow-hidden bg-white/60",
        tableHead: "bg-teal-50 border-b border-teal-100",
        tableHeadText: "uppercase font-bold text-[10px] text-teal-700",
        tableRow: "border-b border-teal-50 hover:bg-teal-50/50",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+','A'].includes(g) ? 'text-teal-600' : 'text-teal-900/50'}`,
        sectionTitle: "text-sm font-bold text-teal-800 mb-1 uppercase tracking-wider pl-2 border-l-4 border-teal-400",
        aiSection: "mb-2 bg-teal-50 p-2 rounded-xl border border-teal-200 text-teal-800",
        remarksSection: "p-2 bg-white border border-teal-200 rounded-xl min-h-[40px] text-sm text-teal-900",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-teal-800/40"
    },
    oceanic: {
        container: "bg-cyan-50 p-8 shadow-2xl text-cyan-950 text-xs",
        font: "font-sans",
        headerWrapper: "bg-gradient-to-r from-cyan-600 to-blue-600 text-white p-2 rounded-lg mb-2 shadow-lg",
        schoolTitle: "text-xl font-bold uppercase tracking-wide",
        subTitle: "text-xs text-cyan-100 uppercase tracking-widest mt-1 opacity-80",
        infoCard: "bg-white p-2 rounded-lg shadow-sm border-l-4 border-cyan-500 mb-2",
        infoLabel: "text-xs font-bold text-cyan-500 uppercase mb-1",
        infoValue: "text-sm font-bold text-slate-700",
        tableContainer: "mb-2 bg-white rounded-lg shadow-sm overflow-hidden",
        tableHead: "bg-cyan-700 text-white",
        tableHeadText: "uppercase font-bold text-[10px] text-white tracking-wider",
        tableRow: "border-b border-cyan-50 even:bg-cyan-50/30",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+','A'].includes(g) ? 'text-cyan-600' : 'text-slate-600'}`,
        sectionTitle: "text-sm font-bold text-cyan-800 mb-1 flex items-center gap-2",
        aiSection: "mb-2 bg-white p-2 border-l-4 border-blue-500 shadow-sm text-slate-600",
        remarksSection: "p-3 bg-white border border-cyan-200 rounded min-h-[40px] text-sm",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-cyan-800 text-xs"
    },
    crimson: {
        container: "bg-[#fff5f5] p-8 shadow-2xl text-red-950 border-[2px] border-red-900 text-xs",
        font: "font-serif",
        headerWrapper: "text-center mb-2 border-b-2 border-red-900 pb-2",
        schoolTitle: "text-xl font-bold text-red-900 uppercase tracking-widest",
        subTitle: "text-[10px] font-bold text-red-700 uppercase tracking-[0.3em] mt-2",
        infoCard: "border-y border-red-200 bg-white py-2 mb-2",
        infoLabel: "text-xs font-bold text-red-800/60 uppercase mb-1 text-center",
        infoValue: "text-sm font-bold text-red-950 text-center",
        tableContainer: "mb-2 border border-red-900",
        tableHead: "bg-red-900 text-white border-b border-red-900",
        tableHeadText: "uppercase font-bold text-[10px] text-red-50 tracking-widest",
        tableRow: "border-b border-red-200 bg-white",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+','A'].includes(g) ? 'text-red-700' : 'text-slate-500'}`,
        sectionTitle: "text-center text-sm font-bold text-red-900 mb-1 uppercase tracking-widest",
        aiSection: "mb-2 bg-white p-2 border border-red-200 text-red-900 italic",
        remarksSection: "p-2 bg-white border border-red-900 min-h-[40px] text-sm font-serif",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-red-900/50 font-serif italic"
    },
    sunset: {
        container: "bg-white p-8 shadow-2xl text-slate-800 text-xs",
        font: "font-sans",
        headerWrapper: "bg-gradient-to-r from-orange-500 to-rose-500 text-white p-2 rounded-xl mb-2 shadow-lg",
        schoolTitle: "text-xl font-bold tracking-tight",
        subTitle: "text-orange-100 text-[10px] font-bold uppercase tracking-widest mt-1",
        infoCard: "bg-orange-50 p-2 rounded-xl mb-2 border border-orange-100",
        infoLabel: "text-xs font-bold text-orange-600 uppercase mb-1",
        infoValue: "text-sm font-bold text-slate-800",
        tableContainer: "mb-2 rounded-xl overflow-hidden border border-orange-100",
        tableHead: "bg-orange-100 border-b border-orange-200",
        tableHeadText: "uppercase font-bold text-[10px] text-orange-700",
        tableRow: "border-b border-orange-50 hover:bg-orange-50",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+','A'].includes(g) ? 'text-orange-600' : 'text-slate-500'}`,
        sectionTitle: "text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-rose-600 mb-1",
        aiSection: "mb-2 bg-gradient-to-br from-orange-50 to-rose-50 p-2 rounded-xl border border-orange-100 text-slate-700",
        remarksSection: "p-2 border border-orange-200 rounded-xl bg-white min-h-[40px] text-sm",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-orange-900/40"
    },
    glass: {
        container: "bg-slate-100 p-8 shadow-2xl text-slate-800 text-xs bg-[url('https://images.unsplash.com/photo-1579546929518-9e396f3cc809?auto=format&fit=crop&w=1000&q=80')] bg-cover bg-center print:bg-none",
        font: "font-sans",
        headerWrapper: "backdrop-blur-xl bg-white/70 p-2 rounded-2xl mb-2 border border-white/50 shadow-sm flex items-center justify-between",
        schoolTitle: "text-xl font-bold text-slate-800",
        subTitle: "text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1",
        infoCard: "backdrop-blur-md bg-white/60 p-2 rounded-2xl mb-2 border border-white/50",
        infoLabel: "text-xs font-bold text-slate-500 uppercase mb-1",
        infoValue: "text-sm font-bold text-slate-900",
        tableContainer: "mb-2 backdrop-blur-md bg-white/80 rounded-2xl overflow-hidden border border-white/50",
        tableHead: "bg-white/50 border-b border-white",
        tableHeadText: "uppercase font-bold text-[10px] text-slate-600",
        tableRow: "border-b border-slate-100/50 hover:bg-white/50",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+','A'].includes(g) ? 'text-indigo-600' : 'text-slate-500'}`,
        sectionTitle: "text-sm font-bold text-slate-700 mb-1 uppercase tracking-wide backdrop-blur-sm bg-white/40 inline-block px-3 py-1 rounded-lg",
        aiSection: "mb-2 backdrop-blur-xl bg-white/70 p-2 rounded-2xl border border-white/50 text-slate-700",
        remarksSection: "p-2 backdrop-blur-md bg-white/60 border border-white/50 rounded-2xl min-h-[40px] text-sm",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-slate-600 backdrop-blur-sm bg-white/30 p-2 rounded-xl"
    },
    "classic-blue": {
        container: "bg-white p-8 shadow-2xl text-slate-900 border-2 border-blue-900 outline-0 m-0 text-xs",
        font: "font-serif",
        headerWrapper: "text-center mb-2 border-b-2 border-blue-900 pb-2 mx-0",
        schoolTitle: "text-xl font-bold text-blue-900 uppercase tracking-widest",
        subTitle: "text-[10px] font-bold text-blue-800 uppercase tracking-widest mt-2",
        infoCard: "mx-0 mb-2 border border-blue-200 p-2 bg-blue-50/30",
        infoLabel: "text-xs font-bold text-blue-800/70 uppercase mb-1 text-center",
        infoValue: "text-sm font-bold text-blue-950 text-center",
        tableContainer: "mx-0 mb-2 border-2 border-blue-900",
        tableHead: "bg-blue-900 text-white border-b-2 border-blue-900",
        tableHeadText: "uppercase font-bold text-[10px] text-white tracking-widest",
        tableRow: "border-b border-blue-200",
        tableRowAlt: "",
        gradeBadge: (g) => `font-bold ${['A+','A'].includes(g) ? 'text-blue-800 underline' : 'text-slate-500'}`,
        sectionTitle: "text-center text-sm font-bold text-blue-900 mb-1 uppercase tracking-widest border-b border-blue-200 w-1/3 mx-auto pb-2",
        aiSection: "mx-0 mb-2 p-2 border-l-4 border-blue-900 bg-slate-50 text-slate-800 italic",
        remarksSection: "mx-0 p-2 border border-blue-900 min-h-[40px] text-sm font-serif",
        footer: "grid grid-cols-3 gap-8 mt-auto pt-2 text-center text-blue-900/60 font-serif italic mx-0"
    }
};


export const ReportPreview: React.FC<ReportPreviewProps> = ({ data, template = 'default', id = "printable-report", gapAdjust = -80 }) => {
    // State for header settings fetched from Firebase
    const [headerSettings, setHeaderSettings] = useState<HeaderSettings | null>(null);
    const [schoolID, setSchoolID] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'standard' | 'cbc'>('standard');

    const toggleControl = (
        <div className="print:hidden flex items-center justify-end gap-2 mb-4 p-2 bg-slate-100 rounded-lg w-full max-w-[210mm] mx-auto border border-slate-200">
            <Label htmlFor="cbc-mode" className={`font-bold cursor-pointer ${viewMode === 'standard' ? 'text-indigo-600' : 'text-slate-500'}`}>Standard</Label>
            <Switch id="cbc-mode" checked={viewMode === 'cbc'} onCheckedChange={(c) => setViewMode(c ? 'cbc' : 'standard')} />
            <Label htmlFor="cbc-mode" className={`font-bold cursor-pointer ${viewMode === 'cbc' ? 'text-indigo-600' : 'text-slate-500'}`}>CBC Mode</Label>
        </div>
    );



    // Get schoolID from authenticated user
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const idTokenResult = await user.getIdTokenResult();
                    const claims = idTokenResult.claims;
                    
                    // Check default affiliation first
                    const defaultAff = claims.defaultAffiliation as { role: string; schoolID: string | null } | undefined;
                    if (defaultAff && (defaultAff.role === 'school_staff' || defaultAff.role === 'admin' || defaultAff.role === 'owner') && defaultAff.schoolID) {
                        setSchoolID(defaultAff.schoolID);
                        return;
                    }

                    // Fallback to affiliations array
                    const affiliations = Array.isArray(claims.affiliations) ? claims.affiliations : [];
                    const staffAffiliation = affiliations.find((aff: any) => 
                        (aff.role === 'school_staff' || aff.role === 'admin' || aff.role === 'owner') && aff.schoolID
                    );
                    
                    if (staffAffiliation?.schoolID) {
                        setSchoolID(staffAffiliation.schoolID);
                    }
                } catch (error) {
                    console.error("Error fetching user claims:", error);
                }
            }
        });
        return () => unsubscribe();
    }, []);

    // Listen to header settings and school details from Firebase
    useEffect(() => {
        if (!schoolID) return;

        // Listen to header settings
        const headerSettingsRef = doc(db, `schools/${schoolID}/headerSettings/default`);
        const schoolDetailsRef = doc(db, `schools/${schoolID}/schoolDetails/main`);

        let headerData: Partial<HeaderSettings> = {};
        let schoolName = INITIAL_HEADER_SETTINGS.schoolName.text;
        let motto = INITIAL_HEADER_SETTINGS.motto.text;
        let address = INITIAL_HEADER_SETTINGS.address.text;
        let logoUrl = INITIAL_HEADER_SETTINGS.logo.url;
        let phone = '';
        let email = '';

        const updateHeaderSettings = () => {
            // Combine address, phone, and email
            let combinedAddress = address;
            if (phone || email) {
                let contactLine = '';
                if (phone) contactLine += `Phone: ${phone}`;
                if (email) contactLine += (contactLine ? ' | ' : '') + `Email: ${email}`;
                combinedAddress = address ? `${address}\n${contactLine}` : contactLine;
            }

            setHeaderSettings({
                ...INITIAL_HEADER_SETTINGS,
                ...headerData,
                schoolName: { ...INITIAL_HEADER_SETTINGS.schoolName, ...(headerData.schoolName || {}), text: schoolName },
                motto: { ...INITIAL_HEADER_SETTINGS.motto, ...(headerData.motto || {}), text: motto },
                address: { ...INITIAL_HEADER_SETTINGS.address, ...(headerData.address || {}), text: combinedAddress },
                logo: { ...INITIAL_HEADER_SETTINGS.logo, ...(headerData.logo || {}), url: logoUrl },
            });
        };

        // Subscribe to header settings changes
        const unsubscribeHeader = onSnapshot(headerSettingsRef, (doc) => {
            if (doc.exists()) {
                headerData = doc.data() as Partial<HeaderSettings>;
            }
            updateHeaderSettings();
        }, (error) => {
            console.error("Error listening to header settings:", error);
        });

        // Subscribe to school details changes
        const unsubscribeSchool = onSnapshot(schoolDetailsRef, (doc) => {
            if (doc.exists()) {
                const data = doc.data();
                if (data.name) schoolName = data.name;
                if (data.motto) motto = data.motto;
                if (data.address) address = data.address;
                if (data.logoUrl) logoUrl = data.logoUrl;
                if (data.phone) phone = data.phone;
                if (data.email) email = data.email;
            }
            updateHeaderSettings();
        }, (error) => {
            console.error("Error listening to school details:", error);
        });

        return () => {
            unsubscribeHeader();
            unsubscribeSchool();
        };
    }, [schoolID]);

    // Ensure data exists to prevent crashes, but don't show error UI if user dislikes it
    if (!data || !Array.isArray(data.subjects)) {
        return null; 
    }
    const totalScore = data.subjects.reduce((acc, curr) => acc + curr.score, 0);
    const totalMax = data.subjects.reduce((acc, curr) => acc + curr.total, 0);
    const overallPercentage = totalMax > 0 ? ((totalScore / totalMax) * 100).toFixed(1) : '0';
    // Use fetched header settings from Firebase, fallback to data.headerSettings
    const activeHeaderSettings = headerSettings || data.headerSettings;
    const shouldUseCustomHeader = activeHeaderSettings?.isEnabled;
    
    if (viewMode === 'cbc') {
        return (
            <div className="flex flex-col items-center w-full">
                {toggleControl}
                <CBCReportTemplate 
                    data={data} 
                    activeHeaderSettings={activeHeaderSettings} 
                    id={id} 
                    gapAdjust={gapAdjust}
                    template={template} 
                />
            </div>
        );
    }
    




  // --- LAYOUT: SIDEBAR ---
  if (template === 'sidebar') {
    return (
      <div className="flex flex-col items-center w-full">
        {toggleControl}
        <div id={id} className="bg-white w-full max-w-[210mm] min-h-[297mm] mx-auto shadow-2xl print:shadow-none print:w-full print:max-w-none text-slate-800 flex overflow-hidden print:overflow-visible print:m-0 print:h-auto print:text-sm">
        {/* Left Sidebar */}
        <div className="w-1/3 bg-slate-900 text-white p-6 print:p-6 flex flex-col">
            <div className="mb-6 print:mb-4">
                <div className="w-20 h-20 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3 border-4 border-slate-600">
                    {activeHeaderSettings?.logo.url ? (
                        <img src={activeHeaderSettings.logo.url} className="w-full h-full object-cover rounded-full" alt="Logo"/>
                    ) : (
                         <User size={40} className="text-slate-400" />
                    )}
                </div>
                <h2 className="text-xl font-bold text-center leading-tight">{data.name}</h2>
                <p className="text-center text-slate-400 text-xs mt-1">{data.studentId}</p>
            </div>

            <div className="space-y-4 mb-8 flex-1">
                 <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-0.5">Grade Level</label>
                    <p className="text-base font-semibold">{data.gradeLevel}</p>
                 </div>
                 <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-0.5">Academic Year</label>
                    <p className="text-base font-semibold">{data.academicYear}</p>
                 </div>
                 <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-0.5">Attendance</label>
                    <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500" style={{width: `${data.attendancePercentage}%`}}></div>
                        </div>
                        <span className="font-bold text-sm">{data.attendancePercentage}%</span>
                    </div>
                 </div>
                 <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-0.5">Class Teacher</label>
                    <p className="text-sm text-slate-300">{data.classTeacher}</p>
                 </div>
            </div>

            <div className="pt-6 border-t border-slate-800 text-center text-[10px] text-slate-500">
                Generated by EduTrack
            </div>
        </div>

        {/* Right Main Content */}
        <div className="w-2/3 p-8 print:p-6 flex flex-col">
            {shouldUseCustomHeader && activeHeaderSettings ? (
                <div dangerouslySetInnerHTML={{ __html: generatePdfHeader(activeHeaderSettings) }} />
            ) : (
                <div className="mb-6 border-b border-slate-100 pb-4">
                    <h1 className="text-xl font-bold text-indigo-900 uppercase tracking-wide leading-tight">{data.schoolName}</h1>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Official Progress Report</p>
                </div>
            )}

            <div className="mb-6 flex-1">
                <table className="w-full text-sm text-left">
                    <thead className="text-slate-400 border-b border-slate-200">
                        <tr>
                            <th className="py-2 font-bold uppercase text-[10px]">Subject</th>
                            <th className="py-2 text-center font-bold uppercase text-[9px]">C1</th>
                            <th className="py-2 text-center font-bold uppercase text-[9px]">C2</th>
                            <th className="py-2 text-center font-bold uppercase text-[9px]">Ex</th>
                            <th className="py-2 text-right font-bold uppercase text-[10px]">Tot</th>
                            <th className="py-2 text-center font-bold uppercase text-[10px]">Grd</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {data.subjects.map((subject) => {
                            const grade = calculateGrade(subject.score, subject.total);
                            return (
                                <tr key={subject.id}>
                                    <td className="py-2 print:py-1 font-medium text-slate-700 text-xs">
                                        {subject.name}
                                        {subject.teacherRemarks && <div className="text-[9px] text-slate-400 font-normal italic leading-none mt-0.5 block truncate max-w-[150px]">{subject.teacherRemarks}</div>}
                                    </td>
                                    <td className="py-2 print:py-1 text-center text-slate-500 text-xs">{subject.cat1}</td>
                                    <td className="py-2 print:py-1 text-center text-slate-500 text-xs">{subject.cat2}</td>
                                    <td className="py-2 print:py-1 text-center text-slate-500 text-xs">{subject.exam}</td>
                                    <td className="py-2 print:py-1 text-right font-mono text-slate-600 font-bold text-xs">{subject.score}</td>
                                    <td className={`py-2 print:py-1 text-center font-bold text-xs ${defaultGradeColor(grade)}`}>{grade}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot className="border-t-2 border-slate-800">
                         <tr>
                            <td className="py-3 font-bold text-slate-800 text-xs" colSpan={4}>OVERALL</td>
                            <td className="py-3 text-right font-bold text-slate-800 text-sm">{totalScore}</td>
                            <td className="py-3 text-center font-bold text-indigo-600 text-sm">{overallPercentage}%</td>
                         </tr>
                    </tfoot>
                </table>
            </div>

             {/* AI Insights Section - Compact */}
             {data.aiSummary && (
                <div className="mb-4 bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                    <h3 className="text-[10px] font-bold text-indigo-800 uppercase mb-1">Performance Analysis</h3>
                    <p className="text-[10px] text-slate-600 leading-snug text-justify">{data.aiSummary}</p>
                </div>
            )}

             {/* Principal's Remarks */}
             <div className="mb-6">
                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Principal's Remarks</h3>
                <div className="text-xs text-slate-600 italic border-l-2 border-slate-300 pl-3 leading-snug">
                    {data.principalRemark || "No remarks."}
                </div>
             </div>

             <div className="grid grid-cols-2 gap-8 mt-auto">
                 <div>
                     <div className="border-b border-slate-300 mb-1 h-6"></div>
                     <div className="text-[9px] uppercase font-bold text-slate-400">Principal Signature</div>
                 </div>
                 <div>
                     <div className="border-b border-slate-300 mb-1 h-6"></div>
                     <div className="text-[9px] uppercase font-bold text-slate-400">Date</div>
                 </div>
             </div>
        </div>
      </div>
    </div>
    );
  }

  // --- LAYOUT: GRID CARDS ---
  if (template === 'grid') {
      return (
        <div className="flex flex-col items-center w-full">
        {toggleControl}
        <div id={id} className="bg-slate-50 w-full max-w-[210mm] min-h-[297mm] mx-auto p-8 print:p-6 shadow-2xl print:shadow-none print:w-full print:max-w-none text-slate-800 flex flex-col print:overflow-visible print:m-0 print:h-auto print:text-sm">
             {/* Header */}
             {shouldUseCustomHeader && activeHeaderSettings ? (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                   <div dangerouslySetInnerHTML={{ __html: generatePdfHeader(activeHeaderSettings) }} />
                </div>
             ) : (
                <div className="text-center mb-6 print:mb-4">
                     <div className="inline-block p-2 bg-white rounded-full shadow-md mb-2 text-indigo-600">
                         <School size={24} />
                     </div>
                     <h1 className="text-xl font-bold text-slate-800">{data.schoolName}</h1>
                     <div className="flex justify-center gap-3 mt-2 text-xs font-medium text-slate-500">
                         <span className="bg-white px-2 py-0.5 rounded shadow-sm">{data.academicYear}</span>
                         <span className="bg-white px-2 py-0.5 rounded shadow-sm">{data.gradeLevel}</span>
                     </div>
                </div>
             )}

            {/* Student Bar */}
            <div className="bg-white p-3 rounded-xl shadow-sm flex items-center justify-between mb-6 print:mb-4 border border-slate-200">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600">
                        <User size={16} />
                    </div>
                    <div>
                        <div className="text-[10px] text-slate-400 font-bold uppercase">Student</div>
                        <div className="font-bold text-slate-800 text-sm">{data.name}</div>
                    </div>
                </div>
                 <div className="text-right hidden sm:block">
                    <div className="text-[10px] text-slate-400 font-bold uppercase">ID</div>
                    <div className="font-mono text-slate-600 text-sm">{data.studentId}</div>
                 </div>
                 <div className="text-right">
                    <div className="text-[10px] text-slate-400 font-bold uppercase">Attendance</div>
                    <div className={`font-bold text-sm ${data.attendancePercentage > 90 ? 'text-green-600' : 'text-orange-500'}`}>{data.attendancePercentage}%</div>
                 </div>
            </div>

            {/* Grid Content */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6 print:mb-4">
                {data.subjects.map(subject => {
                    const grade = calculateGrade(subject.score, subject.total);
                    return (
                        <div key={subject.id} className="bg-white p-3 rounded-xl shadow-sm border border-slate-100 flex flex-col">
                            <div className="flex justify-between items-start mb-2">
                                <h3 className="font-bold text-slate-700 text-xs truncate pr-1">{subject.name}</h3>
                                <span className={`text-xs font-bold ${defaultGradeColor(grade)}`}>{grade}</span>
                            </div>
                            
                            <div className="flex gap-1 mb-2">
                                <div className="flex-1 bg-slate-50 rounded p-0.5 text-center">
                                    <div className="text-[6px] uppercase text-slate-400 font-bold">C1</div>
                                    <div className="text-[10px] font-mono">{subject.cat1}</div>
                                </div>
                                <div className="flex-1 bg-slate-50 rounded p-0.5 text-center">
                                    <div className="text-[6px] uppercase text-slate-400 font-bold">C2</div>
                                    <div className="text-[10px] font-mono">{subject.cat2}</div>
                                </div>
                                <div className="flex-1 bg-slate-50 rounded p-0.5 text-center">
                                    <div className="text-[6px] uppercase text-slate-400 font-bold">Ex</div>
                                    <div className="text-[10px] font-mono">{subject.exam}</div>
                                </div>
                            </div>

                            <div className="text-lg font-light text-slate-800 mb-1 text-center">
                                {subject.score}<span className="text-[10px] text-slate-400 font-normal">/{subject.total}</span>
                            </div>
                            <p className="text-[9px] text-slate-400 italic mt-auto line-clamp-1 border-t border-slate-100 pt-1 text-center">
                                {subject.teacherRemarks || "-"}
                            </p>
                        </div>
                    )
                })}
            </div>

            {/* Summary Card */}
            <div className="bg-indigo-900 text-white p-4 rounded-xl shadow-md mb-6 print:mb-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                 <div>
                     <div className="text-indigo-300 text-[10px] font-bold uppercase mb-0.5">Total Score</div>
                     <div className="text-xl font-bold">{totalScore}</div>
                 </div>
                 <div>
                     <div className="text-indigo-300 text-[10px] font-bold uppercase mb-0.5">Percentage</div>
                     <div className="text-xl font-bold">{overallPercentage}%</div>
                 </div>
                 <div className="md:col-span-2 border-t md:border-t-0 md:border-l border-indigo-700 pt-2 md:pt-0 md:pl-4">
                     <div className="text-indigo-300 text-[10px] font-bold uppercase mb-0.5">Result Status</div>
                     <div className="text-sm font-medium flex items-center gap-2">
                         {parseFloat(overallPercentage) >= 50 ? 
                            <><Sparkles size={14} className="text-yellow-400" /> Promoted to Next Grade</> : 
                            <span className="text-red-300">Academic Review Required</span>
                         }
                     </div>
                 </div>
            </div>

            {/* AI & Principal */}
            <div className="grid md:grid-cols-2 gap-4 mb-6 print:mb-4">
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                    <h3 className="font-bold text-slate-800 mb-2 text-xs flex items-center gap-2">
                        <Sparkles size={12} className="text-indigo-500"/> AI Insights
                    </h3>
                    <p className="text-[10px] text-slate-600 leading-relaxed text-justify">{data.aiSummary || 'No analysis generated.'}</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col">
                     <h3 className="font-bold text-slate-800 mb-2 text-xs">Principal's Remarks</h3>
                     <p className="text-xs text-slate-600 italic flex-1 leading-snug">{data.principalRemark}</p>
                     <div className="mt-2 pt-2 border-t border-slate-100 flex justify-between items-end">
                        <div>
                            <div className="text-[9px] text-slate-400">Signature</div>
                            <div className="h-6 w-24 border-b border-slate-300"></div>
                        </div>
                        <div className="text-[9px] text-slate-400">{new Date().toLocaleDateString()}</div>
                     </div>
                </div>
            </div>

            <div className="mt-auto text-center text-[10px] text-slate-300 pb-2">
                Report generated automatically via EduTrack System
            </div>
        </div>
        </div>
      );
  }

  // --- STANDARD LAYOUT (Skinned) ---
  // Select the theme config based on template ID, fallback to default
  const theme = THEMES[template] || THEMES.default;

    return (
        <div className="flex flex-col items-center w-full">
            {toggleControl}
            <>
            <style>{`
                @media print {
                    @page {
                        margin: 0mm;
                        size: auto;
                    }
                    html, body {
                        background: white !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        min-width: 0 !important;
                        min-height: 0 !important;
                    }
                    #printable-report {
                        margin: 0 !important;
                        box-shadow: none !important;
                        min-width: 0 !important;
                        min-height: 0 !important;
                        width: 100% !important;
                        height: auto !important;
                        page-break-inside: avoid !important;
                    }
                    #printable-report * {
                        color-adjust: exact !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                }
            `}</style>
            <div id={id} className={`${theme.container} w-full max-w-[210mm] min-h-[297mm] mx-auto flex flex-col ${theme.font} relative print:shadow-none print:w-full print:max-w-none print:overflow-visible print:m-0 print:h-auto`}>
                {/* Conditional Rendering for Creative Theme Decoration */}
                {template === 'creative' && (
                        <div className="absolute top-0 right-0 w-48 h-48 bg-purple-200 rounded-bl-full opacity-20 pointer-events-none"></div>
                )}

                {/* Header - Custom or Theme Default */}
                {shouldUseCustomHeader && activeHeaderSettings ? (
                        <div dangerouslySetInnerHTML={{ __html: generatePdfHeader(activeHeaderSettings) }} />
                ) : (
                        <div className={theme.headerWrapper}>
                            <div className="flex items-center gap-4">
                                    {template !== 'minimal' && template !== 'formal' && template !== 'elegant' && template !== 'rose' && template !== 'blossom' && template !== 'royal' && template !== 'slate' && template !== 'mint' && template !== 'oceanic' && template !== 'crimson' && template !== 'sunset' && template !== 'glass' && template !== 'classic-blue' && (
                                            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white shadow-sm
                                                    ${template === 'nature' ? 'bg-emerald-600' : 
                                                        template === 'creative' ? 'bg-purple-600' :
                                                        template === 'bold' ? 'bg-black' :
                                                        template === 'tech' ? 'bg-slate-800 border border-green-500 text-green-500' :
                                                        'bg-indigo-900'
                                                    }`}>
                                                    <School size={32} />
                                            </div>
                                    )}
                                    {/* Custom Icons/Logos for new themes */}
                                    {template === 'rose' && <div className="text-rose-900"><School size={40} /></div>}
                                    {template === 'blossom' && <div className="text-pink-400 bg-pink-100 p-3 rounded-full"><School size={32} /></div>}
                                    {template === 'royal' && <div className="text-yellow-500 border-2 border-yellow-500 p-2 rounded-full"><School size={32} /></div>}
                                    {template === 'slate' && <div className="text-slate-900"><School size={48} strokeWidth={3} /></div>}
                                    {template === 'mint' && <div className="text-teal-600 bg-teal-100 p-3 rounded-xl"><School size={32} /></div>}
                                    {template === 'oceanic' && <div className="text-white bg-cyan-600/30 p-2 rounded backdrop-blur-sm"><School size={32} /></div>}
                                    {template === 'crimson' && <div className="text-red-900"><School size={48} /></div>}
                                    {template === 'sunset' && <div className="text-white bg-white/20 p-2 rounded-lg"><School size={32} /></div>}
                                    {template === 'glass' && <div className="text-slate-700 bg-white/50 p-3 rounded-2xl backdrop-blur-md shadow-sm"><School size={32} /></div>}
                                    {template === 'classic-blue' && <div className="text-blue-900 mb-2"><School size={48} /></div>}


                                    <div className={template === 'formal' || template === 'elegant' || template === 'classic-blue' || template === 'crimson' || template === 'blossom' ? 'w-full' : ''}>
                                            <h1 className={theme.schoolTitle}>{data.schoolName}</h1>
                                            <p className={theme.subTitle}>Official Student Progress Report</p>
                                    </div>
                            </div>
                            {/* Academic Year and date removed for print compactness */}
                        </div>
                )}

                <div 
                    className={shouldUseCustomHeader ? "" : theme.infoCard}
                    style={{ marginTop: `${gapAdjust}px` }}
                >
                     {template === 'creative' && (
                             <div className="col-span-full text-center text-purple-400 text-xs font-bold uppercase mb-2 tracking-widest">Student Profile</div>
                     )}
                    <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 ${template === 'formal' || template === 'elegant' || template === 'classic-blue' ? 'divide-x divide-slate-200/50' : ''}`}>
                            <div className={template === 'formal' || template === 'elegant' || template === 'classic-blue' ? 'px-4' : ''}>
                                    <div className={theme.infoLabel}>
                                            {template !== 'minimal' && template !== 'formal' && <User size={12} />} Student Name
                                    </div>
                                    <div className={theme.infoValue}>{data.name || '—'}</div>
                            </div>
                            <div className={template === 'formal' || template === 'elegant' || template === 'classic-blue' ? 'px-4' : ''}>
                                    <div className={theme.infoLabel}>
                                            {template !== 'minimal' && template !== 'formal' && <GraduationCap size={12} />} Student ID
                                    </div>
                                    <div className={theme.infoValue}>{data.studentId || '—'}</div>
                            </div>
                             <div className={template === 'formal' || template === 'elegant' || template === 'classic-blue' ? 'px-4' : ''}>
                                    <div className={theme.infoLabel}>
                                            {template !== 'minimal' && template !== 'formal' && <Award size={12} />} Class Level
                                    </div>
                                    <div className={theme.infoValue}>{data.classLevel || data.gradeLevel || '—'}</div>
                            </div>
                            <div className={template === 'formal' || template === 'elegant' || template === 'classic-blue' ? 'px-4' : ''}>
                                    <div className={theme.infoLabel}>
                                            {template !== 'minimal' && template !== 'formal' && <Calendar size={12} />} Term
                                    </div>
                                    <div className={theme.infoValue}>{data.term || data.academicYear || '—'}</div>
                            </div>
                    </div>
                    {/* Second row with Position */}
                    {/* Second row with Positions */}
                    {(data.classPosition || data.position) && (
                        <div className={`mt-3 pt-3 border-t ${template === 'formal' || template === 'elegant' || template === 'classic-blue' ? 'border-slate-200/50' : 'border-slate-100'}`}>
                            <div className="flex items-center justify-around">
                                {/* Class Position */}
                                <div className="flex flex-col items-center">
                                    <span className={theme.infoLabel}>Class Position</span>
                                    <span className={`${theme.infoValue} font-bold text-lg`}>
                                        {data.classPosition || data.position}/{data.classTotal || data.totalStudents}
                                    </span>
                                </div>
                                
                                {/* Stream Position (Overall) - Only show if available */}
                                {(data.overallPosition && data.overallTotal) && (
                                    <>
                                        <div className="h-8 w-px bg-slate-200 dark:bg-slate-700"></div>
                                        <div className="flex flex-col items-center">
                                            <span className={theme.infoLabel}>Stream Position</span>
                                            <span className={`${theme.infoValue} font-bold text-lg`}>
                                                {data.overallPosition}/{data.overallTotal}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Academic Performance Table */}
                <div className="mb-2">
                    <h3 className={theme.sectionTitle}>Academic Performance</h3>
                    <div className={theme.tableContainer}>
                            <table className="w-full text-sm text-left">
                                    <thead className={theme.tableHead}>
                                            <tr>
                                                    <th className={`py-1 px-4 w-1/4 ${theme.tableHeadText}`}>Subject</th>
                                                    {/* Dynamic exam type columns */}
                                                    {data.examTypes && data.examTypes.length > 0 ? (
                                                        data.examTypes.map(et => (
                                                            <th key={et.id} className={`py-1 px-2 text-center w-16 ${theme.tableHeadText}`}>{et.name} ({et.weight}%)</th>
                                                        ))
                                                    ) : (
                                                        <>
                                                            <th className={`py-1 px-2 text-center w-12 ${theme.tableHeadText}`}>C1</th>
                                                            <th className={`py-1 px-2 text-center w-12 ${theme.tableHeadText}`}>C2</th>
                                                            <th className={`py-1 px-2 text-center w-12 ${theme.tableHeadText}`}>Ex</th>
                                                        </>
                                                    )}
                                                    <th className={`py-1 px-4 text-center w-16 ${theme.tableHeadText}`}>Total</th>
                                                    <th className={`py-1 px-12 text-center w-12 ${theme.tableHeadText}`}>Grade</th>
                                                    <th className={`py-1 px-4 ${theme.tableHeadText}`}>Remarks</th>
                                            </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                            {data.subjects.map((subject) => {
                                                    const grade = calculateGrade(subject.score, subject.total);
                                                    return (
                                                            <tr key={subject.id} className={theme.tableRow}>
                                                                    <td className={`py-1 px-4 font-medium text-xs ${template === 'tech' ? 'text-green-300' : ''}`}>{subject.name}</td>
                                                                    {/* Dynamic exam type scores */}
                                                                    {data.examTypes && data.examTypes.length > 0 ? (
                                                                        data.examTypes.map(et => (
                                                                            <td key={et.id} className="py-1 px-2 text-center text-slate-500 font-mono text-xs">
                                                                                {subject.scores?.[et.name] ?? '-'}
                                                                            </td>
                                                                        ))
                                                                    ) : (
                                                                        <>
                                                                            <td className="py-1 px-2 text-center text-slate-500 font-mono text-xs">{subject.cat1 ?? '-'}</td>
                                                                            <td className="py-1 px-2 text-center text-slate-500 font-mono text-xs">{subject.cat2 ?? '-'}</td>
                                                                            <td className="py-1 px-2 text-center text-slate-500 font-mono text-xs">{subject.exam ?? '-'}</td>
                                                                        </>
                                                                    )}
                                                                    <td className="py-1 px-4 text-center font-bold font-mono text-xs">{subject.score}</td>
                                                                    <td className={`py-1 px-4 text-center text-xs ${theme.gradeBadge(grade)}`}>{grade}</td>
                                                                    <td className="py-1 px-4 opacity-70 italic text-[10px] truncate max-w-[200px]">{subject.teacherRemarks || '-'}</td>
                                                            </tr>
                                                    );
                                            })}
                                    </tbody>
                                    <tfoot className={`${template === 'minimal' ? 'border-t border-black' : 'bg-slate-50/50'} border-t`}>
                                            <tr>
                                                    <td className="py-3 px-4 uppercase text-[10px] tracking-wider font-bold" colSpan={4}>Overall Result</td>
                                                    <td className="py-3 px-4 text-center text-base font-bold">{totalScore}</td>
                                                    <td className="py-3 px-4 text-center text-base font-bold" colSpan={2}>{overallPercentage}%</td>
                                            </tr>
                                    </tfoot>
                            </table>
                    </div>
                </div>

                {/* AI Insights Section */}
                {data.aiSummary && (
                    <div className={theme.aiSection}>
                            {template === 'creative' && (
                                     <div className="absolute -top-2 -left-2">
                                             <Sparkles className="text-yellow-400 fill-yellow-400" size={20}/>
                                     </div>
                            )}
                            <div className={`flex items-center gap-2 mb-2 ${template === 'tech' ? 'text-green-500' : 'text-indigo-700'}`}>
                                    {template !== 'minimal' && template !== 'formal' && <Sparkles size={14} className={template === 'bold' ? 'text-black' : 'text-purple-500'} />}
                                    <h3 className="font-bold uppercase tracking-wide text-xs">Academic Analysis</h3>
                            </div>
                            <div className="text-xs leading-relaxed text-justify whitespace-pre-line">
                                    {data.aiSummary}
                            </div>
                    </div>
                )}

                {/* Principal's Remarks */}
                <div className="mb-2">
                     <h3 className={theme.sectionTitle}>Principal's Remarks</h3>
                     <div className={theme.remarksSection}>
                            {data.principalRemark || "No additional remarks."}
                     </div>
                </div>

                {/* Footer Signatures */}
                <div className={theme.footer}>
                    <div className="text-center">
                            <div className={`border-b mb-1 h-6 ${template === 'minimal' || template === 'bold' ? 'border-black' : template === 'tech' ? 'border-green-800' : 'border-slate-300'}`}></div>
                            <div className="text-[10px] uppercase font-bold opacity-50 tracking-wider">Class Teacher</div>
                            <div className="text-xs font-serif mt-0.5 opacity-80">{data.classTeacher || 'Signature'}</div>
                    </div>
                    <div className="text-center">
                             <div className={`border-b mb-1 h-6 flex items-end justify-center pb-1 ${template === 'minimal' || template === 'bold' ? 'border-black' : template === 'tech' ? 'border-green-800' : 'border-slate-300'}`}>
                                    <span className="text-[10px] opacity-50">{new Date().toLocaleDateString()}</span>
                             </div>
                            <div className="text-[10px] uppercase font-bold opacity-50 tracking-wider">Date of Issue</div>
                    </div>
                    <div className="text-center">
                            <div className={`border-b mb-1 h-6 ${template === 'minimal' || template === 'bold' ? 'border-black' : template === 'tech' ? 'border-green-800' : 'border-slate-300'}`}></div>
                            <div className="text-[10px] uppercase font-bold opacity-50 tracking-wider">Principal</div>
                    </div>
                </div>

                <div className="text-center mt-2 print:hidden opacity-30 text-[10px]">
                        <p>Generated by EduTrack System</p>
                </div>
            </div>
        </>
        </div>
    );
};


export default ReportPreview;
